import { SchedulerPreference } from '@demind-inc/core';

import { FirestoreRoutineActivity } from '../types';
import { usePreferences, useSchedulerRoutine } from '../queries';
import {
  useUpdateSchedulerPref,
  useUpdateRoutine,
  useAddRoutine,
  useHideRoutine,
} from '../mutations';
import { useAuthContext } from '../context';

export const usePrepareScheduler = () => {
  const { user } = useAuthContext();
  const { preferenceId = '', routineId = '' } = user;
  const {
    routine,
    isLoading: isRoutineLoading,
    error: routineError,
  } = useSchedulerRoutine({
    routineId,
  });

  // Fetch preference data
  const {
    schedulerPreferences,
    isLoading: isPreferenceLoading,
    error: preferenceError,
  } = usePreferences({
    preferenceId,
  });

  // Get mutation hooks
  const { addRoutine, isPending: isAddingRoutine } = useAddRoutine();
  const { updateRoutine, isPending: isUpdatingRoutine } = useUpdateRoutine();
  const { updateSchedulerPref, isPending: isUpdatingSchedulerPref } = useUpdateSchedulerPref();
  const { hideRoutine, isPending: isHidingRoutine } = useHideRoutine();

  // Helper function to add a new routine activity
  const handleAddRoutineActivity = async (routineActivity: FirestoreRoutineActivity) => {
    if (!routineId) return;
    return addRoutine({ routineId, routineActivity });
  };

  // Helper function to update an existing routine activity
  const handleUpdateRoutineActivity = async (
    activityId: string,
    routineActivity: Partial<FirestoreRoutineActivity>
  ) => {
    if (!routineId) return;
    return updateRoutine({ routineId, activityId, routineActivity, preferenceId });
  };

  // Helper function to update scheduler preferences
  const handleUpdatePreferences = async (preferences: Partial<SchedulerPreference>) => {
    if (!preferenceId) return;

    return updateSchedulerPref({ preferenceId, preferences });
  };

  return {
    // Data
    routine,
    schedulerPreferences,

    // Loading states
    isRoutineLoading,
    isPreferenceLoading,
    isAddingRoutine,
    isUpdatingRoutine,
    isUpdatingSchedulerPref,

    // Errors
    routineError,
    preferenceError,

    // Actions
    addRoutineActivity: handleAddRoutineActivity,
    updateRoutineActivity: handleUpdateRoutineActivity,
    updatePreferences: handleUpdatePreferences,
    hideRoutine,
  };
};
